import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Search, Sparkles, Map, MapPin, ArrowRight, X, Loader2, Clock, TrendingUp, Store } from "lucide-react";

import { BACKEND_URL } from "@/config/runtime";

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

// Recent searches helpers
const RECENT_SEARCHES_KEY = "campyid_recent_searches";
const MAX_RECENT = 5;

const getRecentSearches = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || "[]");
  } catch {
    return [];
  }
};

const addRecentSearch = (query) => {
  if (!query.trim()) return;
  const recent = getRecentSearches().filter(s => s.toLowerCase() !== query.toLowerCase());
  recent.unshift(query.trim());
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
};

const removeRecentSearch = (query) => {
  const recent = getRecentSearches().filter(s => s !== query);
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent));
};

// Icon for suggestion type
const SuggestionIcon = ({ type }) => {
  switch (type) {
    case "category": return <Sparkles className="w-4 h-4 text-[#2D5A3D]" />;
    case "subcategory": return <Sparkles className="w-4 h-4 text-[#4A6855]" />;
    case "vendor": return <Store className="w-4 h-4 text-[#2D5A3D]" />;
    case "location": return <MapPin className="w-4 h-4 text-[#2D5A3D]" />;
    default: return <Search className="w-4 h-4 text-[#2D5A3D]" />;
  }
};

const TypeBadge = ({ type }) => {
  const labels = { category: "Category", subcategory: "Subcategory", vendor: "Vendor", location: "Area" };
  const colors = {
    category: "bg-emerald-100 text-emerald-700",
    subcategory: "bg-teal-100 text-teal-700",
    vendor: "bg-stone-100 text-stone-600",
    location: "bg-blue-100 text-blue-700"
  };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${colors[type] || "bg-stone-100 text-stone-600"}`}>
      {labels[type] || type}
    </span>
  );
};

export const LiveSearch = ({ 
  placeholder = "Search vendors, services...", 
  className = "",
  onSearch,
  autoFocus = false,
  size = "default",
  isHeroSearch = false,
  categoryId = null
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [parsed, setParsed] = useState(null);
  const [trending, setTrending] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const debouncedQuery = useDebounce(query, 200);

  // Reset trending when category changes
  useEffect(() => {
    trendingFetched.current = false;
    setTrending([]);
  }, [categoryId]);
  // Load recent searches on mount
  useEffect(() => {
    setRecentSearches(getRecentSearches());
  }, []);

  // Fetch trending on first focus
  const trendingFetched = useRef(false);
  const fetchTrending = useCallback(async () => {
    if (trendingFetched.current) return;
    trendingFetched.current = true;
    try {
      const url = categoryId 
        ? `${BACKEND_URL}/api/search/trending?category_id=${categoryId}`
        : `${BACKEND_URL}/api/search/trending`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setTrending(data.trending || []);
      }
    } catch {}
  }, [categoryId]);

  // Fetch autocomplete suggestions
  const fetchSuggestions = useCallback(async (searchQuery) => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const url = `${BACKEND_URL}/api/search/autocomplete?q=${encodeURIComponent(searchQuery)}&limit=8`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.suggestions || []);
        setParsed(data.parsed || null);
      }
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch results when debounced query changes
  useEffect(() => {
    fetchSuggestions(debouncedQuery);
  }, [debouncedQuery, fetchSuggestions]);

  // Calculate dropdown position for portal (hero search only)
  useEffect(() => {
    if (isHeroSearch && isOpen && inputRef.current) {
      const updatePosition = () => {
        const rect = inputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 8,
          left: rect.left,
          width: rect.width
        });
      };
      
      updatePosition();
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isHeroSearch, isOpen]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !inputRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Determine what's shown in dropdown
  const showingSuggestions = query.trim().length >= 2 && suggestions.length > 0;
  const showingEmpty = query.trim().length >= 2 && !loading && suggestions.length === 0;
  const showingIdleState = !query.trim() && (recentSearches.length > 0 || trending.length > 0);

  // Build search URL preserving category context
  const buildSearchUrl = (searchQuery) => {
    const params = new URLSearchParams({ q: searchQuery });
    if (categoryId) params.set("category_id", categoryId);
    return `/search?${params.toString()}`;
  };

  // Execute search — uses onSearch callback if available (stays on page), otherwise navigates
  const executeSearch = (searchQuery) => {
    addRecentSearch(searchQuery);
    if (onSearch) {
      onSearch(searchQuery);
      setIsOpen(false);
      setQuery(searchQuery);
    } else {
      navigate(buildSearchUrl(searchQuery));
      setIsOpen(false);
      setQuery("");
    }
  };

  // Build unified item list for keyboard navigation
  const getNavigableItems = () => {
    if (showingSuggestions) return suggestions;
    if (showingIdleState) {
      const items = [];
      recentSearches.forEach(s => items.push({ text: s, type: "recent", url: buildSearchUrl(s) }));
      trending.forEach(t => items.push({ ...t, type: `trending_${t.type}` }));
      return items;
    }
    return [];
  };

  const navigableItems = getNavigableItems();

  // Keyboard navigation
  const handleKeyDown = (e) => {
    const totalItems = navigableItems.length;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < navigableItems.length) {
        const item = navigableItems[selectedIndex];
        if (item.type === "recent") {
          executeSearch(item.text);
        } else {
          addRecentSearch(query || item.text);
          navigate(item.url);
        }
        setIsOpen(false);
        setQuery("");
      } else if (query.trim()) {
        executeSearch(query);
        if (onSearch) onSearch(query);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setIsOpen(true);
    setSelectedIndex(-1);
  };

  const handleFocus = () => {
    setIsOpen(true);
    setRecentSearches(getRecentSearches());
    fetchTrending();
  };

  const handleClear = () => {
    setQuery("");
    setSuggestions([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleSuggestionClick = (item) => {
    addRecentSearch(query || item.text);
    if (item.url) {
      navigate(item.url);
    } else {
      navigate(buildSearchUrl(item.text));
    }
    setIsOpen(false);
    setQuery("");
  };

  const handleRecentClick = (text) => {
    executeSearch(text);
  };

  const handleRemoveRecent = (e, text) => {
    e.stopPropagation();
    removeRecentSearch(text);
    setRecentSearches(getRecentSearches());
  };

  const navigateToSearch = () => {
    if (query.trim()) {
      executeSearch(query);
    }
  };

  // Calculate max dropdown height to keep it in viewport
  const [dropdownMaxH, setDropdownMaxH] = useState(400);
  useEffect(() => {
    if (!isOpen || !inputRef.current) return;
    const updateMaxH = () => {
      const rect = inputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - 16; // 16px padding from bottom
      setDropdownMaxH(Math.max(200, spaceBelow));
    };
    updateMaxH();
    window.addEventListener('scroll', updateMaxH, { passive: true });
    window.addEventListener('resize', updateMaxH);
    return () => {
      window.removeEventListener('scroll', updateMaxH);
      window.removeEventListener('resize', updateMaxH);
    };
  }, [isOpen]);

  const showDropdown = isOpen && (showingSuggestions || showingEmpty || showingIdleState || loading);

  const sizeClasses = size === "large" 
    ? "pl-12 pr-12 py-4 text-base rounded-full" 
    : "pl-10 pr-10 py-2 text-sm rounded-xl";

  const iconSize = size === "large" ? "w-5 h-5" : "w-4 h-4";

  // Dropdown content — shared between portal and inline
  const dropdownContent = (
    <div data-testid="search-dropdown">
      {/* Loading */}
      {loading && !showingSuggestions && query.trim().length >= 2 && (
        <div className="p-4 text-center text-[#2D5A3D]">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-1" />
          <span className="text-sm">Searching...</span>
        </div>
      )}

      {/* Empty state - no results */}
      {showingEmpty && (
        <div className="p-4 text-center text-[#2D5A3D]">
          <p className="text-sm mb-2">No results for "<strong>{query}</strong>"</p>
          <button
            onClick={navigateToSearch}
            className="text-[#2D5A3D] hover:underline text-sm font-medium"
          >
            Search all listings →
          </button>
        </div>
      )}

      {/* Idle state: recent + trending */}
      {showingIdleState && !query.trim() && (
        <>
          {recentSearches.length > 0 && (
            <div>
              <div className="px-4 py-2 flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-stone-400" />
                <span className="text-xs font-semibold text-stone-400 uppercase tracking-wide">Recent</span>
              </div>
              {recentSearches.map((text, idx) => (
                <button
                  key={`recent-${idx}`}
                  onClick={() => handleRecentClick(text)}
                  className={`w-full px-4 py-2.5 flex items-center gap-3 hover:bg-green-50 transition-colors text-left ${
                    selectedIndex === idx ? "bg-green-50" : ""
                  }`}
                >
                  <Clock className="w-4 h-4 text-stone-300 flex-shrink-0" />
                  <span className="flex-1 text-sm text-[#1E3A2F] truncate">{text}</span>
                  <button
                    onClick={(e) => handleRemoveRecent(e, text)}
                    className="p-1 text-stone-300 hover:text-stone-500 rounded-full hover:bg-stone-100"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </button>
              ))}
              <div className="border-t border-stone-100" />
            </div>
          )}
          {trending.length > 0 && (
            <div>
              <div className="px-4 py-2 flex items-center gap-2">
                <TrendingUp className="w-3.5 h-3.5 text-[#2D5A3D]" />
                <span className="text-xs font-semibold text-[#2D5A3D] uppercase tracking-wide">
                  {categoryId ? "Inspiring Listings" : "Popular"}
                </span>
              </div>
              {trending.map((item, idx) => {
                const navIdx = recentSearches.length + idx;
                return (
                  <button
                    key={`trending-${idx}`}
                    onClick={() => handleSuggestionClick(item)}
                    className={`w-full px-4 py-2.5 flex items-center gap-3 hover:bg-green-50 transition-colors text-left ${
                      selectedIndex === navIdx ? "bg-green-50" : ""
                    }`}
                  >
                    {item.type === "vendor" && item.image ? (
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-green-100">
                        <img
                          src={item.image}
                          alt={item.text}
                          className="w-full h-full object-cover"
                          style={item.focal_point ? { objectPosition: `${item.focal_point.x}% ${item.focal_point.y}%` } : {}}
                        />
                      </div>
                    ) : (
                      <SuggestionIcon type={item.type} />
                    )}
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-[#1E3A2F] truncate block">{item.text}</span>
                      {item.location && (
                        <span className="text-xs text-stone-400 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />{item.location}
                        </span>
                      )}
                    </div>
                    {!categoryId && <TypeBadge type={item.type} />}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Autocomplete suggestions */}
      {showingSuggestions && (
        <div>
          {/* Parsed query context hint */}
          {parsed && (parsed.location || parsed.price_filter) && (
            <div className="px-4 py-2 bg-stone-50 border-b border-stone-100 flex items-center gap-2 text-xs text-stone-500">
              <Search className="w-3 h-3" />
              <span>Searching <strong className="text-[#1E3A2F]">{parsed.search_term || query}</strong></span>
              {parsed.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> near <strong className="text-[#1E3A2F]">{parsed.location}</strong>
                </span>
              )}
              {parsed.price_filter && (
                <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                  {parsed.price_filter.join(", ")}
                </span>
              )}
            </div>
          )}

          {/* Interleaved results: best subcategory, then vendors, with categories/subcategories compact */}
          {(() => {
            const cats = suggestions.filter(s => s.type === "category" || s.type === "subcategory");
            const vendors = suggestions.filter(s => s.type === "vendor");
            const locations = suggestions.filter(s => s.type === "location");

            // Interleave: first cat, then vendors, remaining cats at bottom
            const ordered = [];
            if (cats.length > 0) ordered.push(cats[0]);
            ordered.push(...vendors);
            ordered.push(...cats.slice(1));
            ordered.push(...locations);

            return ordered.map((item, idx) => {
              const globalIdx = suggestions.indexOf(item);
              const isCatItem = item.type === "category" || item.type === "subcategory";
              const isLocItem = item.type === "location";

              if (isCatItem) {
                // Compact category/subcategory row
                return (
                  <button
                    key={`${item.type}-${idx}`}
                    onClick={() => handleSuggestionClick(item)}
                    className={`w-full px-4 py-2 flex items-center gap-2 hover:bg-green-50 transition-colors text-left ${
                      selectedIndex === globalIdx ? "bg-green-50" : ""
                    }`}
                    data-testid={`suggestion-${item.type}-${idx}`}
                  >
                    <Sparkles className="w-3.5 h-3.5 text-[#2D5A3D] flex-shrink-0" />
                    <span className="text-sm text-[#1E3A2F] truncate">{item.text}</span>
                    {item.parent && <span className="text-xs text-stone-400 truncate">in {item.parent}</span>}
                    <TypeBadge type={item.type} />
                    <ArrowRight className="w-3 h-3 text-stone-300 flex-shrink-0 ml-auto" />
                  </button>
                );
              }

              if (isLocItem) {
                // Compact location row
                return (
                  <button
                    key={`location-${idx}`}
                    onClick={() => handleSuggestionClick(item)}
                    className={`w-full px-4 py-2 flex items-center gap-2 hover:bg-green-50 transition-colors text-left ${
                      selectedIndex === globalIdx ? "bg-green-50" : ""
                    }`}
                  >
                    <MapPin className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                    <span className="text-sm text-[#1E3A2F] truncate">{item.text}</span>
                    <ArrowRight className="w-3 h-3 text-stone-300 flex-shrink-0 ml-auto" />
                  </button>
                );
              }

              // Full vendor row with thumbnail
              return (
                <button
                  key={`vendor-${idx}`}
                  onClick={() => handleSuggestionClick(item)}
                  className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-green-50 transition-colors text-left ${
                    selectedIndex === globalIdx ? "bg-green-50" : ""
                  }`}
                  data-testid={`suggestion-vendor-${idx}`}
                >
                  <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-green-100">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.text}
                        className="w-full h-full object-cover"
                        style={item.focal_point ? { objectPosition: `${item.focal_point.x}% ${item.focal_point.y}%` } : {}}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Map className="w-5 h-5 text-[#2D5A3D]" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#1E3A2F] truncate">{item.text}</p>
                    {item.location && (
                      <div className="flex items-center gap-1 text-xs text-[#2D5A3D]">
                        <MapPin className="w-3 h-3" />
                        <span>{item.location}</span>
                      </div>
                    )}
                  </div>
                  <ArrowRight className="w-4 h-4 text-stone-300 flex-shrink-0" />
                </button>
              );
            });
          })()}

          {/* View all results link */}
          {query.trim() && (
            <button
              onClick={navigateToSearch}
              className="w-full px-4 py-3 bg-green-50 hover:bg-green-100 transition-colors text-center border-t border-stone-200"
              data-testid="view-all-results-btn"
            >
              <span className="text-[#2D5A3D] font-semibold text-sm">
                View all results for "{query}" →
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <Search className={`absolute left-4 top-1/2 -translate-y-1/2 ${iconSize} text-[#2D5A3D]`} />
        <Input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className={`${sizeClasses} border-2 border-stone-200 focus:border-[#6B8E6B] bg-white ${className}`}
          data-testid="live-search-input"
        />
        {query && (
          <button
            onClick={handleClear}
            className={`absolute right-4 top-1/2 -translate-y-1/2 ${iconSize} text-[#2D5A3D] hover:text-[#1E3A2F]`}
            data-testid="clear-search-btn"
          >
            <X className={iconSize} />
          </button>
        )}
        {loading && (
          <Loader2 className={`absolute right-10 top-1/2 -translate-y-1/2 ${iconSize} text-[#2D5A3D] animate-spin`} />
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        isHeroSearch ? (
          createPortal(
            <div
              ref={dropdownRef}
              className="bg-white rounded-xl shadow-2xl border border-stone-200 overflow-hidden overflow-y-auto"
              style={{ 
                position: 'fixed',
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                maxHeight: dropdownMaxH,
                zIndex: 99999
              }}
            >
              {dropdownContent}
            </div>,
            document.body
          )
        ) : (
          <div
            ref={dropdownRef}
            className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-stone-200 overflow-hidden overflow-y-auto z-50"
            style={{ maxHeight: dropdownMaxH }}
          >
            {dropdownContent}
          </div>
        )
      )}
    </div>
  );
};
